import { Alert, Input, Select, Typography } from 'antd';
import type { AuthType } from '@core/entities/ApiRequest';

const AUTH_TYPE_OPTIONS: { label: string; value: AuthType }[] = [
  { label: 'Inherit from parent', value: 'inherit' },
  { label: 'No auth', value: 'none' },
  { label: 'Bearer token', value: 'bearer' },
  { label: 'Basic auth', value: 'basic' },
  { label: 'Digest auth', value: 'digest' },
  { label: 'API key', value: 'api_key' },
  { label: 'JWT bearer', value: 'jwt' },
  { label: 'AWS Signature v4', value: 'aws_signature' },
  { label: 'OAuth 2.0', value: 'oauth2' },
  { label: 'NTLM', value: 'ntlm' },
];

interface AuthEditorProps {
  authType: AuthType;
  authConfig: Record<string, unknown> | null;
  onChange: (authType: AuthType, authConfig: Record<string, unknown> | null) => void;
}

export const AuthEditor = ({ authType, authConfig, onChange }: AuthEditorProps) => {
  const config = authConfig ?? {};
  const setField = (key: string, value: unknown) => onChange(authType, { ...config, [key]: value });
  const str = (key: string) => (typeof config[key] === 'string' ? (config[key] as string) : '');

  return (
    <div className="flex max-w-xl flex-col gap-3">
      <Select
        size="small"
        value={authType}
        options={AUTH_TYPE_OPTIONS}
        onChange={(value: AuthType) => onChange(value, authConfig)}
        className="w-56"
      />

      {authType === 'inherit' && (
        <Alert
          type="info"
          showIcon
          title="Collection and folder-level auth inheritance isn't implemented yet — this behaves like No auth."
        />
      )}
      {authType === 'none' && (
        <Typography.Text type="secondary">This request does not use authorization.</Typography.Text>
      )}
      {authType === 'ntlm' && (
        <Alert
          type="warning"
          showIcon
          title="NTLM isn't implemented yet — the request will be sent without auth."
        />
      )}

      {authType === 'bearer' && (
        <Labeled label="Token">
          <Input.Password
            size="small"
            value={str('token')}
            onChange={(e) => setField('token', e.target.value)}
          />
        </Labeled>
      )}

      {authType === 'basic' && (
        <>
          <Labeled label="Username">
            <Input
              size="small"
              value={str('username')}
              onChange={(e) => setField('username', e.target.value)}
            />
          </Labeled>
          <Labeled label="Password">
            <Input.Password
              size="small"
              value={str('password')}
              onChange={(e) => setField('password', e.target.value)}
            />
          </Labeled>
        </>
      )}

      {authType === 'digest' && (
        <>
          <Labeled label="Username">
            <Input
              size="small"
              value={str('username')}
              onChange={(e) => setField('username', e.target.value)}
            />
          </Labeled>
          <Labeled label="Password">
            <Input.Password
              size="small"
              value={str('password')}
              onChange={(e) => setField('password', e.target.value)}
            />
          </Labeled>
        </>
      )}

      {authType === 'api_key' && (
        <>
          <Labeled label="Key">
            <Input
              size="small"
              value={str('key')}
              onChange={(e) => setField('key', e.target.value)}
            />
          </Labeled>
          <Labeled label="Value">
            <Input.Password
              size="small"
              value={str('value')}
              onChange={(e) => setField('value', e.target.value)}
            />
          </Labeled>
          <Labeled label="Add to">
            <Select
              size="small"
              value={(config.add_to as string) ?? 'header'}
              options={[
                { label: 'Header', value: 'header' },
                { label: 'Query param', value: 'query' },
              ]}
              onChange={(value) => setField('add_to', value)}
              className="w-40"
            />
          </Labeled>
        </>
      )}

      {authType === 'jwt' && (
        <>
          <Alert type="info" showIcon title="Only HS256 (shared-secret) signing is supported." />
          <Labeled label="Secret">
            <Input.Password
              size="small"
              value={str('secret')}
              onChange={(e) => setField('secret', e.target.value)}
            />
          </Labeled>
          <Labeled label="Payload (JSON)">
            <Input.TextArea
              rows={4}
              value={
                typeof config.payload === 'object' ? JSON.stringify(config.payload, null, 2) : ''
              }
              onChange={(e) => {
                try {
                  setField('payload', JSON.parse(e.target.value || '{}'));
                } catch {
                  // ignore invalid JSON while typing
                }
              }}
            />
          </Labeled>
          <Labeled label="Add to">
            <Select
              size="small"
              value={(config.add_to as string) ?? 'header'}
              options={[
                { label: 'Header', value: 'header' },
                { label: 'Query param', value: 'query' },
              ]}
              onChange={(value) => setField('add_to', value)}
              className="w-40"
            />
          </Labeled>
        </>
      )}

      {authType === 'aws_signature' && (
        <>
          <Labeled label="Access key">
            <Input
              size="small"
              value={str('access_key')}
              onChange={(e) => setField('access_key', e.target.value)}
            />
          </Labeled>
          <Labeled label="Secret key">
            <Input.Password
              size="small"
              value={str('secret_key')}
              onChange={(e) => setField('secret_key', e.target.value)}
            />
          </Labeled>
          <Labeled label="Region">
            <Input
              size="small"
              placeholder="us-east-1"
              value={str('region')}
              onChange={(e) => setField('region', e.target.value)}
            />
          </Labeled>
          <Labeled label="Service">
            <Input
              size="small"
              placeholder="execute-api"
              value={str('service')}
              onChange={(e) => setField('service', e.target.value)}
            />
          </Labeled>
          <Labeled label="Session token (optional)">
            <Input.Password
              size="small"
              value={str('session_token')}
              onChange={(e) => setField('session_token', e.target.value)}
            />
          </Labeled>
        </>
      )}

      {authType === 'oauth2' && (
        <>
          <Alert
            type="info"
            showIcon
            title="Interactive grant flows aren't implemented yet — paste an already-acquired access token."
          />
          <Labeled label="Access token">
            <Input.Password
              size="small"
              value={str('access_token')}
              onChange={(e) => setField('access_token', e.target.value)}
            />
          </Labeled>
        </>
      )}
    </div>
  );
};

const Labeled = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs text-gray-500">{label}</label>
    {children}
  </div>
);
